import type { InvoiceListItemDTO, PaymentReviewReason } from "@ai-crm/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./api";
import { useAdminAuth } from "./auth";

export const paymentButton =
  "rounded-md border px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50";
export const paymentPrimary = `${paymentButton} bg-primary text-primary-foreground`;
export const invoiceStatusLabels: Record<InvoiceListItemDTO["status"], string> =
  {
    AWAITING_PAYMENT: "Ожидает оплаты",
    PAYMENT_REPORTED: "Оплачено, ожидает подтверждения",
    PAID_AWAITING_ACTIVATION: "Оплачен, ожидает активации",
    ACTIVATED: "Активирован",
    EXPIRED: "Срок счёта истёк",
    REQUIRES_REVIEW: "Требует проверки",
    SUPERSEDED: "Заменён новым счётом",
  };
export const paymentReviewLabels: Record<PaymentReviewReason, string> = {
  LATE_PAYMENT: "Поздняя оплата",
  PARTIAL_PAYMENT: "Неполная оплата",
  OVERPAYMENT: "Переплата",
  EXTRA_PAYMENT: "Лишний перевод",
  PAYER_MISMATCH: "Другой плательщик",
  PAYER_UNKNOWN: "Неизвестный плательщик",
  SOURCE_CHANGED: "Изменены условия",
  PERIOD_EXPIRED: "Период закончился",
  PAYMENT_CORRECTION: "Исправление записи",
  MANUAL_REVIEW: "Ручная сверка",
};
export const paymentKeys = {
  account: (adminId: string, accountId: string) =>
    ["admin", adminId, "payments", accountId] as const,
  recipient: (adminId: string) =>
    ["admin", adminId, "payment-recipient"] as const,
};
export function paymentError(error: unknown): string {
  if (!(error instanceof ApiError))
    return "Не удалось получить ответ сервера. Проверьте соединение.";
  if (error.code.includes("IDEMPOTENCY"))
    return "Конфликт идентификатора запроса. Новые операции заблокированы до ручной сверки.";
  if (error.code === "PAYMENT_REVIEW_REQUIRED")
    return "Требуется ручная сверка. Можно отдельно записать поступление для проверки.";
  if (error.status === 409)
    return "Данные или условия изменились. Черновик сохранён; обновите сведения и явно получите новый предпросмотр.";
  if (error.code.startsWith("PAYMENT_RECEIPT_METADATA_")) return error.message;
  if (error.status === 413) return "PDF превышает допустимый размер 10 МиБ.";
  if (error.status === 503)
    return "Сервис временно недоступен. Повторите запрос вручную.";
  return `${error.message} (${error.code})`;
}

/** Each mounted workspace owns its generation: A → B → A cannot revive old work. */
export function usePaymentScope(accountId?: string) {
  const { token, admin } = useAdminAuth();
  const identity = `${admin?.id ?? ""}:${accountId ?? ""}:${token ?? ""}`;
  const state = useRef({ identity, generation: 0, mounted: true });
  if (state.current.identity !== identity) {
    state.current = {
      identity,
      generation: state.current.generation + 1,
      mounted: true,
    };
  }
  useEffect(() => {
    state.current.mounted = true;
    return () => {
      state.current.mounted = false;
      state.current.generation += 1;
    };
  }, [identity]);
  const capture = useCallback(() => {
    const generation = state.current.generation;
    return {
      isCurrent: () =>
        state.current.mounted && generation === state.current.generation,
    };
  }, []);
  const read = useCallback(
    async <T>(signal: AbortSignal, operation: () => Promise<T>): Promise<T> => {
      const scope = capture();
      const value = await operation();
      if (signal.aborted || !scope.isCurrent())
        throw new DOMException("Scope changed", "AbortError");
      return value;
    },
    [capture],
  );
  return { token, adminId: admin?.id ?? "", capture, read, identity };
}

/** A write is never automatically retried or aborted. The original closure/body survives uncertainty. */
export function usePaymentWrite(accountId?: string) {
  const scope = usePaymentScope(accountId);
  const intent = useRef<null | (() => Promise<void>)>(null);
  const inFlight = useRef(false);
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const execute = async () => {
    if (inFlight.current || !intent.current || blocked) return;
    const guard = scope.capture();
    inFlight.current = true;
    setPending(true);
    setError(null);
    try {
      await intent.current();
      if (!guard.isCurrent()) return;
      intent.current = null;
      setUncertain(false);
      setErrorCode(null);
    } catch (caught) {
      if (!guard.isCurrent()) return;
      setErrorCode(caught instanceof ApiError ? caught.code : null);
      if (!(caught instanceof ApiError) || caught.status >= 500) {
        setUncertain(true);
        setError(
          "Результат неизвестен. Повторите тот же запрос; изменение параметров заблокировано.",
        );
      } else {
        intent.current = null;
        setUncertain(false);
        setError(paymentError(caught));
        if (caught.code.includes("IDEMPOTENCY")) setBlocked(true);
      }
    } finally {
      if (guard.isCurrent()) {
        inFlight.current = false;
        setPending(false);
      }
    }
  };
  return {
    pending,
    uncertain,
    blocked,
    error,
    errorCode,
    locked: pending || uncertain || blocked,
    clearError: () => {
      if (!inFlight.current && !uncertain && !blocked) {
        setError(null);
        setErrorCode(null);
      }
    },
    async run<T>(
      body: T,
      send: (body: T) => Promise<unknown>,
      accepted: () => void | Promise<void>,
      rejected?: (error: unknown) => void | Promise<void>,
    ) {
      if (inFlight.current || intent.current || blocked) return;
      const guard = scope.capture();
      intent.current = async () => {
        try {
          await send(body);
        } catch (caught) {
          if (guard.isCurrent()) await rejected?.(caught);
          throw caught;
        }
        if (guard.isCurrent()) await accepted();
      };
      await execute();
    },
    retry: execute,
  };
}

export function usePreviewExpiry(expiresAt: string | undefined): boolean {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (!expiresAt) return;
    const delay = Date.parse(expiresAt) - Date.now();
    if (delay <= 0) return;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(delay + 1, 2147483647),
    );
    return () => window.clearTimeout(timer);
  }, [expiresAt]);
  return expiresAt !== undefined && now >= Date.parse(expiresAt);
}
export function useVisiblePage(): boolean {
  const [visible, setVisible] = useState(document.visibilityState !== "hidden");
  useEffect(() => {
    const changed = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", changed);
    return () => document.removeEventListener("visibilitychange", changed);
  }, []);
  return visible;
}
